// Details drawer of a map: preview, description, rules, the rooms playing it
// and the play buttons (quick play, create a room, solo practice; offline:
// play on this device).

import { useEffect, useRef, useState } from 'preact/hooks';
import { Button } from '../components/Button';
import { ColorText } from '../components/ColorText';
import { Levelshot } from '../components/Levelshot';
import { useApp } from '../context';
import { describeRules, getMapMeta, type MapMeta } from '../maps/mapMeta';
import { mb, useMapEntries } from '../maps/useCatalog';
import { createRoomDialog, net, settings, updateSettings } from '../state';
import { MapMetaChips } from '../components/MapMetaChips';

export function MapDetails({ name, onClose }: { name: string; onClose: () => void }) {
  const app = useApp();
  const s = settings.value;
  const n = net.value;
  const online = n.status === 'online';
  const entry = useMapEntries().find((e) => e.name === name);
  const [localMeta, setLocalMeta] = useState<MapMeta | null | undefined>(undefined);
  const closeRef = useRef<HTMLButtonElement>(null);
  const local = !!entry?.local;

  useEffect(() => {
    if (!local || entry?.info) return;
    let alive = true;
    getMapMeta(app.vfs, name).then((m) => alive && setLocalMeta(m));
    return () => {
      alive = false;
    };
  }, [name, local, !!entry?.info]);

  useEffect(() => {
    // keyboard users land in the drawer
    ((document.querySelector('.drawer .btn-primary') as HTMLElement | null) ?? closeRef.current)?.focus();
  }, [name]);

  if (!entry) return null;
  const meta = entry.meta ?? localMeta ?? undefined;
  const rules = meta ? describeRules(meta.rules) : [];
  const favorite = s.favorites.includes(name);
  const onServer = online && !!entry.info;
  const rooms = n.rooms.filter((r) => r.map === name);

  return (
    <aside class="drawer map-details layer" data-layer="drawer" aria-label={`Map ${name}`}>
      <div class="drawer-hero">
        <Levelshot map={name} eager class="drawer-shot" />
        <Button variant="ghost" icon="close" class="drawer-close" aria-label="Close details" buttonRef={closeRef} onClick={onClose} />
      </div>
      <div class="drawer-body">
        <div class="eyebrow">{onServer ? (local ? 'On this device' : `Download ${mb(entry.info!.bundleSize)}`) : 'Offline'}</div>
        <h2 class="drawer-title">{entry.longname ? <ColorText text={entry.longname} /> : name}</h2>
        <div class="drawer-sub">
          <span class="mono">{name}</span>
          {entry.author && <span> · by {entry.author}</span>}
        </div>
        {entry.briefing && <p class="briefing">{entry.briefing}</p>}
        <MapMetaChips info={entry.info} />

        <div class="drawer-actions">
          {onServer ? (
            <>
              <Button variant="primary" size="lg" icon="play" onClick={() => app.quickPlay(name)}>
                Quick play
              </Button>
              <Button variant="secondary" size="lg" icon="users" onClick={() => (createRoomDialog.value = { map: name })}>
                Create room
              </Button>
              <Button variant="ghost" icon="flag" onClick={() => app.practice(name)} title="A private room of your own; times count on the server">
                Practice solo
              </Button>
            </>
          ) : local ? (
            <Button variant="primary" size="lg" icon="play" onClick={() => app.playOffline(name)}>
              Play offline
            </Button>
          ) : (
            <p class="muted">Connect to the server to play this map.</p>
          )}
          <Button
            variant="ghost"
            icon="star"
            aria-pressed={favorite}
            onClick={() => updateSettings({ favorites: favorite ? s.favorites.filter((f) => f !== name) : [...s.favorites, name] })}
          >
            {favorite ? 'Favorite' : 'Add to favorites'}
          </Button>
        </div>

        {onServer && rooms.length > 0 && (
          <>
            <h3 class="section-title">Rooms on this map</h3>
            <ul class="plain-list">
              {rooms.map((r) => (
                <li key={r.id}>
                  <button type="button" class="list-button" onClick={() => app.joinRoom({ id: r.id })}>
                    <span>
                      {r.name} {r.official && <span class="chip static chip-info">Official</span>}
                    </span>
                    <span class="muted mono">
                      {r.players}/{r.maxPlayers}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        <h3 class="section-title">Rules</h3>
        {!meta && local ? (
          <p class="muted">Reading map…</p>
        ) : (
          <div class="chips">
            {rules.length ? rules.map((r) => <span key={r} class="chip static">{r}</span>) : <span class="muted">Standard ETJump rules</span>}
            <span class="chip static">Infinite stamina</span>
          </div>
        )}
      </div>
    </aside>
  );
}
